/** Same gap as today's right-of-folder placement. */
export const ATLAS_PEEK_GAP = 12;

/** Inset from the canvas edge so the card never kisses the chrome. */
export const ATLAS_PEEK_VIEW_PAD = 8;

const SIDES = ['right', 'left', 'below', 'above'];

const asRect = (folder) => ({
  x: folder.x,
  y: folder.y,
  w: folder.width,
  h: folder.height,
});

const peekRectAt = (x, y, w, h) => ({ x, y, w, h });

export function rectsOverlap(a, b) {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

export function overlapArea(a, b) {
  const w = Math.max(0, Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x));
  const h = Math.max(0, Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y));
  return w * h;
}

export function folderIntersectsView(folder, viewport) {
  return rectsOverlap(asRect(folder), {
    x: viewport.x,
    y: viewport.y,
    w: viewport.w,
    h: viewport.h,
  });
}

/** Estimate card size from peek content (refined by measure after paint). */
export function estimatePeekSize(peek) {
  const w = 220;
  let h = 16;
  if (!peek || peek.empty) {
    h += 22;
  } else {
    for (const g of peek.groups || []) {
      h += 18;
      h += (g.shown?.length || 0) * 28;
      if (g.more > 0) h += 22;
      h += 8;
    }
  }
  h += 32;
  return { w, h };
}

function kissOnSide(side, folder, peekW, peekH, gap) {
  switch (side) {
    case 'right':
      return { x: folder.x + folder.width + gap, y: folder.y };
    case 'left':
      return { x: folder.x - gap - peekW, y: folder.y };
    case 'below':
      return { x: folder.x, y: folder.y + folder.height + gap };
    case 'above':
      return { x: folder.x, y: folder.y - gap - peekH };
    default:
      return { x: folder.x + folder.width + gap, y: folder.y };
  }
}

function viewRangeX(viewport, peekW) {
  const min = viewport.x + ATLAS_PEEK_VIEW_PAD;
  const max = viewport.x + viewport.w - ATLAS_PEEK_VIEW_PAD - peekW;
  return { min, max: Math.max(min, max) };
}

function viewRangeY(viewport, peekH) {
  const min = viewport.y + ATLAS_PEEK_VIEW_PAD;
  const max = viewport.y + viewport.h - ATLAS_PEEK_VIEW_PAD - peekH;
  return { min, max: Math.max(min, max) };
}

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

/**
 * Slide along the kissed edge so the card stays on screen without
 * leaving the folder, when both are possible.
 */
function slideAlongEdge(side, pos, folder, peekW, peekH, viewport) {
  const vx = viewRangeX(viewport, peekW);
  const vy = viewRangeY(viewport, peekH);

  if (side === 'right' || side === 'left') {
    const kissMin = folder.y - peekH;
    const kissMax = folder.y + folder.height;
    const min = Math.max(vy.min, kissMin);
    const max = Math.min(vy.max, kissMax);
    const y = min <= max ? clamp(pos.y, min, max) : clamp(pos.y, vy.min, vy.max);
    return { x: pos.x, y };
  }

  const kissMin = folder.x - peekW;
  const kissMax = folder.x + folder.width;
  const min = Math.max(vx.min, kissMin);
  const max = Math.min(vx.max, kissMax);
  const x = min <= max ? clamp(pos.x, min, max) : clamp(pos.x, vx.min, vx.max);
  return { x, y: pos.y };
}

function clampToViewport(pos, peekW, peekH, viewport) {
  const vx = viewRangeX(viewport, peekW);
  const vy = viewRangeY(viewport, peekH);
  return {
    x: clamp(pos.x, vx.min, vx.max),
    y: clamp(pos.y, vy.min, vy.max),
  };
}

function isFullyInside(rect, viewport) {
  return (
    rect.x >= viewport.x - 0.5 &&
    rect.y >= viewport.y - 0.5 &&
    rect.x + rect.w <= viewport.x + viewport.w + 0.5 &&
    rect.y + rect.h <= viewport.y + viewport.h + 0.5
  );
}

function totalOverlap(peek, others) {
  let sum = 0;
  for (const folder of others) {
    sum += overlapArea(peek, asRect(folder));
  }
  return sum;
}

function stillKisses(side, pos, folder, peekW, peekH, gap) {
  const tol = 1.5;
  if (side === 'right') return Math.abs(pos.x - (folder.x + folder.width + gap)) <= tol;
  if (side === 'left') return Math.abs(pos.x + peekW + gap - folder.x) <= tol;
  if (side === 'below') return Math.abs(pos.y - (folder.y + folder.height + gap)) <= tol;
  if (side === 'above') return Math.abs(pos.y + peekH + gap - folder.y) <= tol;
  return false;
}

function candidateForSide(side, folder, peekW, peekH, viewport, others, gap) {
  let pos = kissOnSide(side, folder, peekW, peekH, gap);
  pos = slideAlongEdge(side, pos, folder, peekW, peekH, viewport);
  pos = clampToViewport(pos, peekW, peekH, viewport);
  const rect = peekRectAt(pos.x, pos.y, peekW, peekH);
  return {
    side,
    x: pos.x,
    y: pos.y,
    overlap: totalOverlap(rect, others),
    fullyInside: isFullyInside(rect, viewport),
    kisses: stillKisses(side, pos, folder, peekW, peekH, gap),
  };
}

/**
 * Place the Files peek so it kisses `folder`, covers as few in-view
 * neighbours as possible, and stays fully inside `viewport`.
 *
 * @param {object} opts
 * @param {{ x: number, y: number, width: number, height: number }} opts.folder
 * @param {Array<{ x: number, y: number, width: number, height: number }>} opts.others
 * @param {{ w: number, h: number }} opts.peekSize
 * @param {{ x: number, y: number, w: number, h: number }} opts.viewport
 * @param {number} [opts.gap]
 */
export function placeAtlasPeek({
  folder,
  others = [],
  peekSize,
  viewport,
  gap = ATLAS_PEEK_GAP,
}) {
  const peekW = Math.max(1, peekSize?.w || 220);
  const peekH = Math.max(1, peekSize?.h || 80);
  const inView = (others || []).filter((f) => folderIntersectsView(f, viewport));

  const candidates = SIDES.map((side) =>
    candidateForSide(side, folder, peekW, peekH, viewport, inView, gap)
  );

  const clear = candidates.find((c) => c.overlap === 0 && c.fullyInside && c.kisses);
  if (clear) {
    return { x: clear.x, y: clear.y, side: clear.side };
  }

  // Boxed in: today's default (right, top-aligned), then slide/flip to fit.
  let pos = kissOnSide('right', folder, peekW, peekH, gap);
  const vy = viewRangeY(viewport, peekH);
  pos = { x: pos.x, y: clamp(pos.y, vy.min, vy.max) };

  if (pos.x + peekW > viewport.x + viewport.w - ATLAS_PEEK_VIEW_PAD) {
    const flipped = kissOnSide('left', folder, peekW, peekH, gap);
    pos = { x: flipped.x, y: pos.y };
  }
  if (pos.x < viewport.x + ATLAS_PEEK_VIEW_PAD) {
    const flipped = kissOnSide('right', folder, peekW, peekH, gap);
    if (flipped.x + peekW <= viewport.x + viewport.w - ATLAS_PEEK_VIEW_PAD) {
      pos = { x: flipped.x, y: pos.y };
    }
  }
  if (pos.y + peekH > viewport.y + viewport.h - ATLAS_PEEK_VIEW_PAD) {
    pos = { x: pos.x, y: clamp(pos.y, vy.min, vy.max) };
  }

  pos = clampToViewport(pos, peekW, peekH, viewport);
  const usedLeft = pos.x + peekW / 2 < folder.x + folder.width / 2;
  return { x: pos.x, y: pos.y, side: usedLeft ? 'left' : 'right' };
}
